import React, { Fragment, useMemo, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Field, Formik } from "formik";
import { Move, ZoomIn, ZoomOut } from "lucide-react";
import CloseIcon from "../../../../assets/icons/close-icon";
import SearchIcon from "../../../../assets/icons/search-icon";
import InputField from "../../../forms/input-field";
import AddButton, { DraftButton } from "../../../buttons/button";
import ConfirmModal from "../../../helper-components/confirm-modal";
import errorToast from "../../../helper-components/error-toast";
import successToast from "../../../helper-components/success-toast";
import warningToast from "../../../helper-components/warning-toast";
import { displayHtmlContent, printHtml } from "../../../helper-components/helper-function";
import {
  APPS_ON_DEVICE,
  buildCodeLabelTsplJob,
  ensureAppsOnDevicePermission,
  printTsplLabels,
  saveThermalPrinter,
} from "../../../helper-components/tsc-label-printer";
import {
  ThermalPrinterPicker,
  useThermalPrinters,
} from "../../../helper-components/thermal-printer-picker";
import { isAdmin } from "../../../../redux/config";
import { TrayLabelFace, trayLabelsPrintHtml } from "./tray-label-face";
import {
  TRAY_BARCODE_RENDER_OPTIONS,
  matchesTraySearch,
  readTrayLabelSettings,
  saveTrayLabelSettings,
  trayCodeValue,
  trayLabelQueue,
  trayLabelSize,
} from "./tray-label";

const DEFAULT_ZOOM = 2;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.1;
// Keep the modal quick for big bulk prints; every label still prints, and a
// search reaches any tray's count.
const MAX_PREVIEW_LABELS = 200;
const MAX_TABLE_ROWS = 100;
const CODE_OPTIONS = [
  { value: "barcode", label: "Barcode" },
  { value: "qr", label: "QR Code" },
];
const TABLE_HEADINGS = ["Tray Name", "Tray Barcode", "No. of Labels"];

const headCellClass =
  "border-b border-[var(--list-table-border-color)] px-[8px] h-[50px] text-left whitespace-nowrap text-[var(--list-table-heading-fg-color)] text-[length:--list-table-heading-font-size] font-[var(--list-table-heading-font-weight)]";
const bodyCellClass =
  "border-b border-[var(--list-table-border-color)] px-[8px] text-left whitespace-nowrap text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)]";
const settingLabelClass = "text-[10px] uppercase tracking-wide text-secondary fw_500";
const zoomButtonClass = "p-2 rounded-md border border-D0D5DD hover:bg-nav-head-bg";

/**
 * Chrome must allow "Apps on device" before the page can reach QZ Tray, so —
 * as for GRN labels — ask before opening the print modal. Resolves whether to
 * open it; a failed check opens it anyway, where the printer picker explains.
 */
export const confirmLabelPrinterAccess = () =>
  ensureAppsOnDevicePermission({
    onPrompt: () =>
      warningToast('Click "Allow" on the browser\'s "Apps on device" prompt to continue'),
  })
    .then((state) => {
      if (state === APPS_ON_DEVICE.DENIED) {
        errorToast(
          'Printing is blocked. Click the icon at the left of the address bar, turn on "Apps on device", reload and try again.'
        );
        return false;
      }
      if (state === APPS_ON_DEVICE.PENDING) {
        errorToast('Allow "Apps on device" in the browser prompt, then print the labels again.');
        return false;
      }
      return true;
    })
    .catch(() => true);

const labelCountsFor = (trays) => trays.map(() => 1);

const modalTitle = (trays) =>
  trays.length === 1
    ? `Print Tray Label (${trays[0]?.tray_name ?? ""})`
    : `Print Tray Labels (${trays.length} trays)`;

function ThermalPreview({ queue, codeType, size }) {
  const shown = queue.slice(0, MAX_PREVIEW_LABELS);
  return (
    <div className="flex flex-col items-center shrink-0" style={{ gap: `${size.gap}mm` }}>
      {shown.map((tray, index) => (
        <TrayLabelFace
          key={index}
          tray={tray}
          codeType={codeType}
          widthMm={size.width}
          heightMm={size.height}
        />
      ))}
      {queue.length > shown.length && (
        <p className="text-[6px] text-secondary">
          Showing the first {shown.length} of {queue.length} labels
        </p>
      )}
    </div>
  );
}

function LabelCountTable({ trays, search, setFieldValue }) {
  const matches = trays
    .map((tray, index) => ({ tray, index }))
    .filter(({ tray }) => matchesTraySearch(tray, search));
  const rows = matches.slice(0, MAX_TABLE_ROWS);
  return (
    <>
      {matches.length > rows.length && (
        <p className="text-xs text-secondary mb-1">
          Showing {rows.length} of {matches.length} trays — search to change another tray's count.
        </p>
      )}
      <div className="max-h-[22vh] overflow-y-auto outline outline-[var(--list-table-border-color)] outline-1 border-radius">
        <table className="w-full border-collapse mt-0">
          <thead className="sticky top-0 bg-[var(--list-table-heading-bg)] z-[10]">
            <tr>
              {TABLE_HEADINGS.map((heading) => (
                <th key={heading} className={headCellClass}>
                  {heading.toUpperCase()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr style={{ height: "8rem" }}>
                <td colSpan={TABLE_HEADINGS.length} className="text-center text-primary">
                  No trays match this name or barcode
                </td>
              </tr>
            ) : (
              rows.map(({ tray, index }) => (
                <tr
                  key={tray.tray_id ?? index}
                  className="hover:bg-[var(--list-table-row-hover-bg)] bg-[var(--list-table-row-bg)] h-[50px]"
                >
                  <td className={bodyCellClass}>{tray.tray_name}</td>
                  <td className={bodyCellClass}>{tray.barcode}</td>
                  <td className={`${bodyCellClass} py-1 w-32`}>
                    <InputField
                      isContainer={false}
                      label=""
                      type="number"
                      min={0}
                      name={`labelCounts[${index}]`}
                      isRequired={false}
                      onBlur={(event) =>
                        setFieldValue(
                          `labelCounts[${index}]`,
                          Math.max(0, Math.floor(Number(event.target.value) || 0))
                        )
                      }
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function SizeField({ name, label, className = "flex flex-col items-start gap-1 w-full" }) {
  return (
    <InputField
      parentClass={className}
      labelClass={settingLabelClass}
      type="number"
      step="any"
      extraAllowedChars="."
      min={0}
      name={name}
      label={label}
      isRequired={false}
      labelWidth="auto"
    />
  );
}

/**
 * The modal body. Mounted only while the modal is open, so every opening
 * starts fresh: counts of 1 per tray and the saved default settings.
 */
function TrayLabelPrintForm({ trays, onClose, onPrinted }) {
  const [printing, setPrinting] = useState(false);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [search, setSearch] = useState("");
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const thermal = useThermalPrinters(true);
  const previewRef = useRef(null);
  const initialValues = useMemo(
    () => ({ ...readTrayLabelSettings(), labelCounts: labelCountsFor(trays) }),
    [trays]
  );
  const isBatch = trays.length > 1;

  const resetZoom = () => {
    setZoom(DEFAULT_ZOOM);
    previewRef.current?.scrollTo(0, 0);
  };

  const print = async (values) => {
    const queue = trayLabelQueue(trays, values.labelCounts);
    if (queue.length === 0) {
      errorToast("Add at least one label to print");
      return;
    }
    const size = trayLabelSize(values);
    const printer = thermal.printer;
    setPrinting(true);
    try {
      if (printer) {
        const labels = queue.map((tray) => ({
          title: tray.tray_name,
          codeValue: trayCodeValue(tray),
        }));
        await printTsplLabels(
          printer,
          buildCodeLabelTsplJob(labels, {
            widthMm: size.width,
            heightMm: size.height,
            gapMm: size.gap,
            codeType: values.codeType,
            renderOptions: TRAY_BARCODE_RENDER_OPTIONS,
          })
        );
        saveThermalPrinter(printer);
        successToast(`Sent ${labels.length} label(s) to ${printer}`);
      } else {
        const html = await trayLabelsPrintHtml(queue, values.codeType, size);
        if (isAdmin) displayHtmlContent(html);
        else printHtml(html, { widthMm: size.width, heightMm: size.height + size.gap });
      }
      onPrinted?.();
      onClose();
    } catch (error) {
      console.error("Print tray labels failed", error);
      errorToast(
        printer
          ? `Could not print to ${printer}. ${error?.message || "Please try again."}`
          : "Could not open the print preview. Please try again."
      );
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Formik initialValues={initialValues} onSubmit={print}>
      {({ values, setFieldValue, handleSubmit, resetForm }) => {
        const queue = trayLabelQueue(trays, values.labelCounts);
        const size = trayLabelSize(values);
        return (
          <>
            <div className="flex justify-between items-center px-6 pt-4 shrink-0">
              <h2 className="text-secondary fw_600 text-lg">{modalTitle(trays)}</h2>
              <button
                type="button"
                className="close-button bg-transparent border-none p-0 hover:bg-787878"
                onClick={onClose}
              >
                <CloseIcon />
              </button>
            </div>

            {trays.length === 0 ? (
              <div className="px-6 py-10 text-center text-secondary text-sm">
                No trays to print
              </div>
            ) : (
              <>
                {isBatch && (
                  <div className="px-6 pt-3 shrink-0 flex items-center justify-between gap-3">
                    <div className="relative w-64">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2">
                        <SearchIcon />
                      </span>
                      <input
                        type="text"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Search by tray name or barcode"
                        className="w-full border border-D0D5DD rounded-md pl-9 pr-3 py-1.5 text-sm text-secondary focus:outline-none focus:border-primary"
                      />
                    </div>
                    <button
                      type="button"
                      className="text-xs text-F51D00 border border-F51D00 rounded-md px-3 py-1.5 fw_500 whitespace-nowrap"
                      onClick={() => setConfirmClearOpen(true)}
                    >
                      Set all Labels Qty as 0
                    </button>
                  </div>
                )}

                <div className="px-6 pt-2 shrink-0">
                  <LabelCountTable trays={trays} search={search} setFieldValue={setFieldValue} />
                </div>

                <ConfirmModal
                  isOpen={confirmClearOpen}
                  title="Set all label quantities to 0?"
                  message="This will set the number of labels to print for every tray in this list to 0."
                  confirmText="Yes"
                  cancelText="No"
                  onConfirm={() => {
                    setFieldValue(
                      "labelCounts",
                      trays.map(() => 0)
                    );
                    setConfirmClearOpen(false);
                  }}
                  onCancel={() => setConfirmClearOpen(false)}
                />

                <div className="flex flex-1 min-h-0 px-6 py-4 gap-4">
                  <div
                    className="flex-1 min-w-0 rounded-lg overflow-auto relative"
                    style={{ backgroundColor: "var(--slide-panel-special-bg)" }}
                    ref={previewRef}
                  >
                    <div
                      className="p-8 flex flex-col items-center gap-6 w-fit"
                      style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}
                    >
                      <ThermalPreview queue={queue} codeType={values.codeType} size={size} />
                    </div>
                  </div>

                  <div className="w-[280px] shrink-0 overflow-y-auto">
                    <div className="flex justify-between items-baseline mb-2">
                      <h3 className="text-secondary fw_600 text-sm">Settings</h3>
                      <div className="text-xs flex gap-2">
                        <button
                          type="button"
                          className="text-primary underline"
                          onClick={() => {
                            if (saveTrayLabelSettings(values)) {
                              successToast("Default settings saved");
                            } else {
                              errorToast("Could not save default settings");
                            }
                          }}
                        >
                          Save as default
                        </button>
                        <span className="text-secondary">|</span>
                        <button
                          type="button"
                          className="text-primary underline"
                          onClick={() =>
                            resetForm({
                              values: {
                                ...readTrayLabelSettings(),
                                labelCounts: labelCountsFor(trays),
                              },
                            })
                          }
                        >
                          Reset
                        </button>
                      </div>
                    </div>

                    <ThermalPrinterPicker {...thermal} />
                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <SizeField name="thermalLabelWidth" label="Label Width (mm)" />
                      <SizeField name="thermalLabelHeight" label="Label Height (mm)" />
                    </div>
                    <SizeField
                      name="thermalGap"
                      label="Gap Between Labels (mm)"
                      className="flex flex-col items-start gap-1 w-full mt-4"
                    />
                  </div>
                </div>

                <div className="border-t border-D0D5DD px-6 py-3 flex items-center justify-between shrink-0">
                  <div>
                    <p className="text-secondary fw_500 text-xs mb-1">Codes:</p>
                    <div className="flex gap-2">
                      {CODE_OPTIONS.map((option) => (
                        <label
                          key={option.value}
                          className={`px-3 py-1 rounded-md border cursor-pointer text-xs whitespace-nowrap ${
                            values.codeType === option.value
                              ? "bg-primary text-white border-primary"
                              : "border-D0D5DD text-secondary"
                          }`}
                        >
                          <Field
                            type="radio"
                            name="codeType"
                            value={option.value}
                            className="hidden"
                          />
                          {option.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      title="Zoom out"
                      onClick={() =>
                        setZoom((z) => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2)))
                      }
                      className={zoomButtonClass}
                    >
                      <ZoomOut size={16} />
                    </button>
                    <span className="text-xs text-secondary w-10 text-center">
                      {Math.round(zoom * 100)}%
                    </span>
                    <button
                      type="button"
                      title="Zoom in"
                      onClick={() =>
                        setZoom((z) => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)))
                      }
                      className={zoomButtonClass}
                    >
                      <ZoomIn size={16} />
                    </button>
                    <button
                      type="button"
                      title="Reset view"
                      onClick={resetZoom}
                      className={zoomButtonClass}
                    >
                      <Move size={16} />
                    </button>
                  </div>
                </div>
              </>
            )}

            <div className="px-6 flex gap-4 py-4 shrink-0">
              <DraftButton onclkFn={onClose} text="Cancel" />
              <AddButton
                isDisabled={queue.length === 0 || printing}
                loading={printing}
                onclkFn={handleSubmit}
                type="submit"
                className="bg-primary text-white px-8 py-2 rounded flex justify-center fw_500 text-sm"
                text={queue.length > 1 ? `Print ${queue.length} Labels` : "Print"}
                prefixIcon={false}
              />
            </div>
          </>
        );
      }}
    </Formik>
  );
}

/**
 * Thermal-only label print for trays — same flow as GRN "Print Barcodes":
 * preview, label size and gap, barcode or QR, then raw TSPL straight to a TSC
 * printer (desktop app or QZ Tray), or the print dialog when none is picked.
 * Each label carries only the tray name and the code of its barcode number.
 * `onPrinted` runs after the labels are sent (not on cancel).
 */
const TrayLabelPrintModal = ({ isOpen, onClose, onPrinted, trays = [] }) => (
  <Transition show={Boolean(isOpen)} as={Fragment}>
    <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={onClose}>
      <div className="flex items-center justify-center min-h-screen px-2 text-center">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-100"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="fixed inset-0 bg-black opacity-60" />
        </Transition.Child>

        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 translate-y-4"
          enterTo="opacity-100 translate-y-0"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-4"
        >
          <div className="mx-auto w-full max-w-[98vw] h-[94vh] my-4 text-left align-middle transition-all transform bg-white shadow-xl rounded-xl flex flex-col overflow-hidden">
            <TrayLabelPrintForm trays={trays} onClose={onClose} onPrinted={onPrinted} />
          </div>
        </Transition.Child>
      </div>
    </Dialog>
  </Transition>
);

export default TrayLabelPrintModal;
